import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import {
  EXPENSE_CATEGORIES,
  EXPENSE_CATEGORY_LABELS,
  formatDate,
  formatMoney,
  type ExpenseCategory,
} from "@mavatech/shared";
import { PageHeader } from "@/components/layout/Layout";
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  Input,
  MoneyInput,
  Select,
  SortTh,
  Textarea,
  useSort,
  LoadError,
} from "@/components/ui";
import { useFeatures } from "@/lib/useFeatures";
import { trpc } from "@/lib/trpc";
import { useAppStore } from "@/stores/app.store";

const MONTHS = [
  "Jänner",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];
const today = () => new Date().toISOString().slice(0, 10);

interface Form {
  id?: string;
  date: string;
  description: string;
  supplier: string;
  category: ExpenseCategory;
  netCents: number;
  note: string;
}
const EMPTY: Form = {
  date: today(),
  description: "",
  supplier: "",
  category: "sonstiges",
  netCents: 0,
  note: "",
};

/** Ausgaben (netto) — kundenspezifisches Modul, per Einstellungen → Module ein-/ausschaltbar. */
export function Expenses() {
  const { expensesVisible, loading } = useFeatures();
  const utils = trpc.useUtils();
  const addToast = useAppStore((s) => s.addToast);
  const now = new Date();
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState("");
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [form, setForm] = useState<Form | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const list = trpc.expense.list.useQuery(
    {
      year: Number(year),
      month: month ? Number(month) : undefined,
      category: (category || undefined) as ExpenseCategory | undefined,
      search: search || undefined,
    },
    { enabled: expensesVisible },
  );
  const { sorted, sort, toggle } = useSort(list.data?.rows ?? [], {
    date: (e) => e.date,
    description: (e) => e.description,
    category: (e) =>
      EXPENSE_CATEGORY_LABELS[e.category as ExpenseCategory] ?? e.category,
    net: (e) => e.netCents,
  });
  const refresh = () => {
    utils.expense.list.invalidate();
    utils.expense.overview.invalidate();
  };
  const onErr = (e: { message: string }) =>
    addToast({ type: "error", title: "Fehler", description: e.message });
  const save = trpc.expense.create.useMutation({
    onSuccess: () => {
      refresh();
      setForm(null);
      addToast({ type: "success", title: "Ausgabe gespeichert" });
    },
    onError: onErr,
  });
  const update = trpc.expense.update.useMutation({
    onSuccess: () => {
      refresh();
      setForm(null);
      addToast({ type: "success", title: "Ausgabe gespeichert" });
    },
    onError: onErr,
  });
  const remove = trpc.expense.remove.useMutation({
    onSuccess: () => {
      refresh();
      addToast({ type: "success", title: "Ausgabe gelöscht" });
    },
    onError: onErr,
  });

  if (loading) return <p className="p-6 text-sm text-text-tertiary">Lädt …</p>;
  if (!expensesVisible) return <Navigate to="/" replace />;

  const years = Array.from({ length: 6 }, (_, i) =>
    String(now.getFullYear() - i),
  );
  const submit = () => {
    if (!form || !form.description.trim()) return;
    const payload = {
      date: new Date(form.date),
      description: form.description.trim(),
      supplier: form.supplier.trim() || null,
      category: form.category,
      netCents: form.netCents,
      note: form.note.trim() || null,
    };
    if (form.id) update.mutate({ id: form.id, ...payload });
    else save.mutate(payload);
  };

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Ausgaben"
        actions={
          <Button
            variant="primary"
            onClick={() => setForm({ ...EMPTY, date: today() })}
          >
            <Plus className="w-4 h-4" /> Neue Ausgabe
          </Button>
        }
      />
      <div className="px-6 py-3 border-b border-border-subtle flex items-center gap-3 flex-wrap">
        <div className="relative max-w-xs flex-1">
          <Search className="w-4 h-4 text-text-tertiary absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            className="pl-8"
            placeholder="Beschreibung, Lieferant …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select
          fullWidth={false}
          className="w-28"
          value={year}
          onChange={(e) => setYear(e.target.value)}
          options={years.map((y) => ({ value: y, label: y }))}
        />
        <Select
          fullWidth={false}
          className="w-36"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          options={[
            { value: "", label: "Ganzes Jahr" },
            ...MONTHS.map((m, i) => ({ value: String(i + 1), label: m })),
          ]}
        />
        <Select
          fullWidth={false}
          className="w-52"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          options={[
            { value: "", label: "Alle Kategorien" },
            ...EXPENSE_CATEGORIES.map((c) => ({
              value: c,
              label: EXPENSE_CATEGORY_LABELS[c],
            })),
          ]}
        />
        <div className="ml-auto text-sm text-text-secondary">
          Summe (netto):{" "}
          <strong className="text-text-primary text-money">
            {formatMoney(list.data?.totalNetCents ?? 0)}
          </strong>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-auto">
        {list.isLoading ? (
          <p className="p-6 text-sm text-text-tertiary">Lädt …</p>
        ) : list.isError ? (
          <LoadError onRetry={() => list.refetch()} />
        ) : !list.data?.rows.length ? (
          <div className="p-10 text-center text-sm text-text-tertiary">
            Keine Ausgaben im gewählten Zeitraum.
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <SortTh
                  label="Datum"
                  sortKey="date"
                  sort={sort}
                  onSort={toggle}
                />
                <SortTh
                  label="Beschreibung / Lieferant"
                  sortKey="description"
                  sort={sort}
                  onSort={toggle}
                />
                <SortTh
                  label="Kategorie"
                  sortKey="category"
                  sort={sort}
                  onSort={toggle}
                />
                <SortTh
                  label="Netto"
                  sortKey="net"
                  sort={sort}
                  onSort={toggle}
                  className="text-right"
                />
                <th />
              </tr>
            </thead>
            <tbody>
              {sorted.map((e) => (
                <tr
                  key={e.id}
                  className="cursor-pointer"
                  onClick={() =>
                    setForm({
                      id: e.id,
                      date: new Date(e.date).toISOString().slice(0, 10),
                      description: e.description,
                      supplier: e.supplier ?? "",
                      category: (e.category as ExpenseCategory) ?? "sonstiges",
                      netCents: e.netCents,
                      note: e.note ?? "",
                    })
                  }
                >
                  <td className="text-text-secondary whitespace-nowrap">
                    {formatDate(e.date as unknown as string)}
                  </td>
                  <td>
                    <div className="font-medium text-text-primary">
                      {e.description}
                    </div>
                    {(e.supplier || e.note) && (
                      <div className="text-xs text-text-tertiary">
                        {[e.supplier, e.note].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </td>
                  <td>
                    <Badge tone="neutral">
                      {EXPENSE_CATEGORY_LABELS[e.category as ExpenseCategory] ??
                        e.category}
                    </Badge>
                  </td>
                  <td className="text-right text-money text-text-primary">
                    {formatMoney(e.netCents)}
                  </td>
                  <td className="text-right whitespace-nowrap">
                    <button
                      className="text-text-tertiary hover:text-text-primary mr-2"
                      title="Bearbeiten"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      className="text-text-tertiary hover:text-danger"
                      title="Löschen"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        setConfirmDelete(e.id);
                      }}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog
        open={!!form}
        onOpenChange={(v) => !v && setForm(null)}
        title={form?.id ? "Ausgabe bearbeiten" : "Neue Ausgabe"}
        wide
        footer={
          <>
            <Button variant="ghost" onClick={() => setForm(null)}>
              Abbrechen
            </Button>
            <Button
              variant="primary"
              onClick={submit}
              loading={save.isPending || update.isPending}
              disabled={!form?.description.trim()}
            >
              Speichern
            </Button>
          </>
        }
      >
        {form && (
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Datum"
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
            <div>
              <label className="label">Betrag netto (€)</label>
              <MoneyInput
                valueCents={form.netCents}
                onChangeCents={(c) =>
                  setForm((f) => (f ? { ...f, netCents: c } : f))
                }
              />
              <p className="mt-1 text-xs text-text-tertiary">
                Ohne Umsatzsteuer.
              </p>
            </div>
            <div className="col-span-2">
              <Input
                label="Beschreibung"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                autoFocus
              />
            </div>
            <Input
              label="Lieferant (optional)"
              value={form.supplier}
              onChange={(e) => setForm({ ...form, supplier: e.target.value })}
            />
            <Select
              label="Kategorie"
              value={form.category}
              onChange={(e) =>
                setForm({
                  ...form,
                  category: e.target.value as ExpenseCategory,
                })
              }
              options={EXPENSE_CATEGORIES.map((c) => ({
                value: c,
                label: EXPENSE_CATEGORY_LABELS[c],
              }))}
            />
            <div className="col-span-2">
              <Textarea
                label="Notiz (optional)"
                rows={2}
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
              />
            </div>
          </div>
        )}
      </Dialog>

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
        title="Ausgabe löschen?"
        message="Die Ausgabe wird entfernt und fließt nicht mehr in die Gewinnübersicht ein."
        confirmLabel="Löschen"
        danger
        onConfirm={() => confirmDelete && remove.mutate({ id: confirmDelete })}
      />
    </div>
  );
}
